/**
 * Client-side mirror of the API's optional feature catalog (ADR-0073, audience taxonomy ADR-0076 —
 * see `apps/api/src/modules/identity/domain/value-objects/household-features.vo.ts`). This is NOT a
 * security boundary: the API's `FeatureGuard` enforces every gate. It only lets the web HIDE surfaces
 * a household has disabled — nav items, route pages, embedded cards — so a turned-off feature is simply
 * invisible rather than a 403 that surfaces as "Something went sideways". Keep in sync with the server.
 */
export const Feature = {
  Chores: 'chores',
  Habits: 'habits',
  Economy: 'economy',
  Cosmetics: 'cosmetics',
  /** Play & progression (the companion + level/XP). Renamed from `companion` in ADR-0076. */
  Play: 'play',
  Quests: 'quests',
  Learning: 'learning',
  ActivityFeed: 'activity_feed',
  Notifications: 'notifications',
  /** Lets a household define its OWN custom roles (ADR-0078). Off by default; super-admin opts in. */
  CustomRoles: 'custom_roles',
} as const;
export type Feature = (typeof Feature)[keyof typeof Feature];

/** Audience a feature serves — mirrors the API's `FEATURE_AUDIENCE` (ADR-0076). Drives the grouped
 *  toggle UI in the platform console (kid-facing / parent tools / system). */
export type FeatureAudience = 'kid' | 'parent' | 'system';

export const FEATURE_AUDIENCE: Readonly<Record<string, FeatureAudience>> = {
  [Feature.Chores]: 'parent',
  [Feature.Habits]: 'parent',
  [Feature.Economy]: 'parent',
  [Feature.Quests]: 'parent',
  [Feature.Learning]: 'kid',
  [Feature.Play]: 'kid',
  [Feature.Cosmetics]: 'kid',
  [Feature.ActivityFeed]: 'system',
  [Feature.Notifications]: 'system',
  [Feature.CustomRoles]: 'parent',
};

/** Stable display order of the audience groups in the toggle UI. */
export const FEATURE_AUDIENCE_ORDER: readonly FeatureAudience[] = ['kid', 'parent', 'system'];

/**
 * Client mirror of the API's `FEATURE_DEPENDENCIES` — a feature → the features it REQUIRES. The server
 * enforces the closure (disabling a dependency disables its dependents); the console only surfaces the
 * relationship as a hint so an operator understands why a toggle is coupled. Keep in sync with the server.
 */
export const FEATURE_DEPENDENCIES: Readonly<Record<string, readonly string[]>> = {
  [Feature.Cosmetics]: [Feature.Economy],
};

/** Client mirror of the API's `DEFAULT_OFF_FEATURES` — flags a household starts WITHOUT (ADR-0078). */
export const DEFAULT_OFF_FEATURES: ReadonlySet<string> = new Set([Feature.CustomRoles]);
